import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
} from '@mui/material';
import { Download, FilterList, Info, CompareArrows } from '@mui/icons-material';
import axios from 'axios';
import { useSettings } from '../../../contexts/SettingsContext';

// Helper function to compare and extract changed values
const getChangedValues = (beforeData: any, afterData: any, changedFields: string[]) => {
  const changes: Array<{ field: string; before: any; after: any }> = [];
  
  if (!beforeData || !afterData) return changes;

  changedFields?.forEach((field) => {
    const beforeValue = beforeData[field];
    const afterValue = afterData[field];
    
    // Deep comparison for objects
    if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
      changes.push({
        field,
        before: beforeValue,
        after: afterValue,
      });
    }
  });

  return changes;
};

// Format value for display
const formatValue = (value: any): string => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

function ActivityLogsPage() {
  const { formatDateTime } = useSettings();
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({
    category: '',
    startDate: '',
    endDate: '',
    actorType: '',
    isAdminUser: '',
  });
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

  useEffect(() => {
    loadLogs();
  }, [page, rowsPerPage]);

  const loadLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        skip: String(page * rowsPerPage),
        limit: String(rowsPerPage),
        ...(filter.category && { category: filter.category }),
        ...(filter.startDate && { start_date: filter.startDate }),
        ...(filter.endDate && { end_date: filter.endDate }),
        ...(filter.isAdminUser && { is_admin_user: filter.isAdminUser }),
      });

      const response = await axios.get(
        `${API_URL}/api/v1/settings/logs?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load logs:', error);
    }
  };

  const handleExport = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${API_URL}/api/v1/settings/logs/export`,
      {
        params: filter,
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    // Handle download
    console.log('Export:', response.data);
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      auth: 'primary',
      data_access: 'info',
      data_modify: 'warning',
      data_delete: 'error',
      security: 'error',
      system: 'default',
    };
    return colors[category] || 'default';
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Activity Logs</Typography>
        <Button startIcon={<Download />} variant="outlined" onClick={handleExport}>
          Export
        </Button>
      </Box>

      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filter.category}
                onChange={(e) => setFilter({ ...filter, category: e.target.value })}
                label="Category"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="auth">Authentication</MenuItem>
                <MenuItem value="data_access">Data Access</MenuItem>
                <MenuItem value="data_modify">Data Modify</MenuItem>
                <MenuItem value="data_delete">Data Delete</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Actor Type</InputLabel>
              <Select
                value={filter.isAdminUser}
                onChange={(e) => setFilter({ ...filter, isAdminUser: e.target.value })}
                label="Actor Type"
              >
                <MenuItem value="">All Users</MenuItem>
                <MenuItem value="true">Admin Users</MenuItem>
                <MenuItem value="false">Regular Users</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={filter.startDate}
              onChange={(e) => setFilter({ ...filter, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={filter.endDate}
              onChange={(e) => setFilter({ ...filter, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={2.4}>
            <Button fullWidth variant="contained" startIcon={<FilterList />} onClick={loadLogs}>
              Apply Filter
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Resource</TableCell>
                <TableCell>Changed Fields</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log: any, index: number) => (
                <TableRow key={log.id || `log-${index}`}>
                  <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {log.actor_name || log.username || log.user_email || (log.user_id ? `User ${log.user_id.substring(0, 8)}...` : 'Anonymous')}
                      </Typography>
                      {log.actor_email && log.actor_email !== log.actor_name && (
                        <Typography variant="caption" color="text.secondary">
                          {log.actor_email}
                        </Typography>
                      )}
                      {(log.actor_type === 'admin_user' || log.is_admin_user) && (
                        <Chip label="Admin" size="small" color="error" sx={{ ml: 1, mt: 0.5 }} />
                      )}
                      {log.actor_type === 'regular_user' && (
                        <Chip label="User" size="small" color="primary" sx={{ ml: 1, mt: 0.5 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {log.target_user_name ? (
                      <Box>
                        <Typography variant="body2">{log.target_user_name}</Typography>
                        {log.target_user_id && (
                          <Typography variant="caption" color="text.secondary">
                            ID: {log.target_user_id.substring(0, 8)}...
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell>
                    <Chip 
                      label={log.category} 
                      color={getCategoryColor(log.category) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{log.resource_type || '-'}</TableCell>
                  <TableCell>
                    {log.changed_fields && log.changed_fields.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {log.changed_fields.map((field: string, idx: number) => (
                          <Chip key={idx} label={field} size="small" variant="outlined" color="info" />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={log.status} 
                      color={log.status === 'success' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{log.ip_address || '-'}</TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setSelectedLog(log);
                        setDetailDialogOpen(true);
                      }}
                    >
                      <Info fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Info />
            <Typography variant="h6">Activity Log Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                {/* Basic Info Section */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Actor</Typography>
                        <Typography variant="body1" fontWeight="500">
                          {selectedLog.actor_name || selectedLog.username || 'N/A'}
                        </Typography>
                        {selectedLog.actor_email && (
                          <Typography variant="caption" color="text.secondary">
                            {selectedLog.actor_email}
                          </Typography>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Target</Typography>
                        <Typography variant="body1" fontWeight="500">
                          {selectedLog.target_user_name || 'N/A'}
                        </Typography>
                        {selectedLog.target_user_id && (
                          <Typography variant="caption" color="text.secondary">
                            ID: {selectedLog.target_user_id}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Action</Typography>
                        <Chip label={selectedLog.action} color="primary" size="small" />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Timestamp</Typography>
                        <Typography variant="body2">{formatDateTime(selectedLog.timestamp)}</Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                        <Typography variant="body2">{selectedLog.description}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Changed Fields Section - Highlighted Comparison */}
                {selectedLog.changed_fields && selectedLog.changed_fields.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <CompareArrows color="warning" />
                        <Typography variant="h6" color="warning.main">
                          Changed Fields ({selectedLog.changed_fields.length})
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                    </Box>

                    {getChangedValues(
                      selectedLog.before_data, 
                      selectedLog.after_data, 
                      selectedLog.changed_fields
                    ).map((change, idx) => (
                      <Paper 
                        key={idx} 
                        variant="outlined" 
                        sx={{ 
                          mb: 2, 
                          overflow: 'hidden',
                          border: '2px solid',
                          borderColor: 'warning.light',
                        }}
                      >
                        <Box sx={{ bgcolor: 'warning.light', px: 2, py: 1 }}>
                          <Typography variant="subtitle1" fontWeight="600" color="warning.dark">
                            {change.field}
                          </Typography>
                        </Box>
                        <Grid container>
                          <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
                            <Box sx={{ p: 2, bgcolor: 'error.lighter', minHeight: '100px' }}>
                              <Typography variant="caption" color="error.main" fontWeight="600" gutterBottom display="block">
                                BEFORE
                              </Typography>
                              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white', mt: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  component="pre" 
                                  sx={{ 
                                    margin: 0, 
                                    whiteSpace: 'pre-wrap', 
                                    wordBreak: 'break-word',
                                    fontFamily: 'monospace',
                                    color: 'error.dark',
                                  }}
                                >
                                  {formatValue(change.before)}
                                </Typography>
                              </Paper>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, bgcolor: 'success.lighter', minHeight: '100px' }}>
                              <Typography variant="caption" color="success.main" fontWeight="600" gutterBottom display="block">
                                AFTER
                              </Typography>
                              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white', mt: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  component="pre" 
                                  sx={{ 
                                    margin: 0, 
                                    whiteSpace: 'pre-wrap', 
                                    wordBreak: 'break-word',
                                    fontFamily: 'monospace',
                                    color: 'success.dark',
                                  }}
                                >
                                  {formatValue(change.after)}
                                </Typography>
                              </Paper>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Grid>
                )}

                {/* Additional Info Section */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">IP Address</Typography>
                        <Typography variant="body2" fontFamily="monospace">
                          {selectedLog.ip_address || 'N/A'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Resource</Typography>
                        <Typography variant="body2">
                          {selectedLog.resource_type || 'N/A'}
                          {selectedLog.resource_name && ` (${selectedLog.resource_name})`}
                        </Typography>
                      </Grid>

                      {selectedLog.compliance_tags && selectedLog.compliance_tags.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Compliance Tags
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {selectedLog.compliance_tags.map((tag: string, idx: number) => (
                              <Chip key={idx} label={tag} size="small" color="warning" />
                            ))}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ActivityLogsPage;
